import * as signalR from "@microsoft/signalr";
import { DashboardStatus } from "@/models/DashboardStatus";
import { PhoneStats } from "@/models/PhoneStats";
import Vue from "vue";
import axios from 'axios';

import DashboardSharedWorker from "@/workers/dashboardWorker.js?sharedworker";
import { UserNotificationViewModel, Notification as NotificationObject } from "@/models/Notification";
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

let isPrimaryTab = false;
let useSystemNotifications: boolean | null = null;
let electLeaderTab: any | null = null;

const processedNotifications = new Set<number>();
const NOTIFICATION_CLEANUP_TIMEOUT = 60000; // Clean up after 60 seconds

const handlePrimaryTab = (): void => {

    const tabCoordinatorChannel = new BroadcastChannel('tab-coordinator');

    const tabId = Math.random().toString(36).substring(2, 9);

    const becomePrimary = (): void => {
        isPrimaryTab = true;
        tabCoordinatorChannel.postMessage({ type: 'PRIMARY', tabId: tabId });
    }

    electLeaderTab = async (): Promise<void> => {
        tabCoordinatorChannel.postMessage({ type: 'ELECTION', tabId: tabId });

        await new Promise<void>(resolve => {
            setTimeout(() => {
                if (!isPrimaryTab) {
                    becomePrimary();
                }
                resolve();
            }, 750);
        });
    }

    tabCoordinatorChannel.onmessage = (event) => {
        const { type, tabId: senderTabId } = event.data;

        if (type === 'ELECTION') {
            if (tabId > senderTabId) {
                tabCoordinatorChannel.postMessage({ type: 'ELECTION_RESPONSE', tabId: tabId });
            }
        } else if (type === 'ELECTION_RESPONSE') {
            isPrimaryTab = false;
        } else if (type === 'PRIMARY') {
            if (senderTabId !== tabId) {
                isPrimaryTab = false;
            }
        }

        if (type === 'PRIMARY_CLOSED' || type === 'PRIMARY_INACTIVE') {
            electLeaderTab();
        }
    };

    window.addEventListener('load', electLeaderTab);

    window.addEventListener('beforeunload', () => {
        if (isPrimaryTab) {
            tabCoordinatorChannel.postMessage({ type: 'PRIMARY_CLOSED' });
        }
    });

    document.addEventListener('visibilitychange', () => {
        if (document.hidden && isPrimaryTab) {
            tabCoordinatorChannel.postMessage({ type: 'PRIMARY_INACTIVE' });
            isPrimaryTab = false;
        } else if (!document.hidden && !isPrimaryTab) {
            electLeaderTab();
        }
    });

    electLeaderTab();
};

const markNotificationRead = (app: Vue, userNotificationId: number): void => {
    try {
        if (userNotificationId <= 0) return;
        app.$http.post(`/notification/mark-read/${userNotificationId}`);
    } catch (err) {
        app.$notification(NotificationOptions.error(err));
    }
}

export const showNotificationToast = (app: Vue, userNotificationId: number, notification: NotificationObject): void => {
    // Invisible clickable overlay that covers the entire toast
    const clickableOverlay = app.$createElement("router-link", {
        props: {
            to: notification.target
        },
        style: {
            position: "absolute",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            zIndex: "10",
            display: "block",
            cursor: "pointer"
        },
        nativeOn: {
            click() {
                markNotificationRead(app, userNotificationId);
            }
        }
    });

    // Build toast body: visible text + invisible overlay
    const bodyText = notification.body || notification.title;
    const bodyChildren: (string | ReturnType<typeof app.$createElement>)[] = [bodyText, clickableOverlay];

    const wrapper = app.$createElement("div", {}, bodyChildren);

    app.$bvToast.toast(wrapper, {
        title: notification.title,
        solid: true,
        toaster: "b-toaster-bottom-right",
        toastClass: "position-relative notification-toast"
    });
};

const showWebNotification = (app: Vue, userNotificationId: number, notification: NotificationObject): void => {
    const systemNotification = new Notification(notification.title, {
        body: notification.phi ? undefined : notification.body,
        tag: notification.id.toString()
    });

    systemNotification.onclick = (): any => {
        window.focus();  // Bring browser window to foreground
        app.$router.push(notification.target);
        markNotificationRead(app, userNotificationId);
    };
}

const handleNotification = async (app: Vue, userNotification: UserNotificationViewModel): Promise<void> => {
    const notification = userNotification.notification;

    console.debug("beginning notification handling...");

    if (processedNotifications.has(userNotification.id)) {
        console.debug(`Notification ${userNotification.id} already processed, skipping duplicate`);
        return;
    }

    processedNotifications.add(userNotification.id);

    // Schedule cleanup to prevent memory leaks
    setTimeout(() => {
        processedNotifications.delete(userNotification.id);
        console.debug(`Cleaned up notification ${userNotification.id} from processed set`);
    }, NOTIFICATION_CLEANUP_TIMEOUT);

    if (notification.modal) {
        app.$bvModal.msgBoxOk(notification.body, {
            title: notification.title,
            centered: true
        });
        return;
    }

    const supportsSystemNotifications = "Notification" in window;
    if (!supportsSystemNotifications) {
        showNotificationToast(app, userNotification.id, notification);
        return;
    }

    if (useSystemNotifications === null) {
        useSystemNotifications = !(await app.$http.get<boolean>("/configuration/DisableWebNotifications")).data;
    }

    if (!useSystemNotifications) {
        showNotificationToast(app, userNotification.id, notification);
        return;
    }

    const notificationPermission = await Notification.requestPermission();

    if (notificationPermission !== "granted") {
        showNotificationToast(app, userNotification.id, notification);
        return;
    }

    await electLeaderTab();

    if (!isPrimaryTab) {
        console.debug("not primary tab, not showing web notification");
        return;
    }

    showWebNotification(app, userNotification.id, notification);
};

const handleSharedWorker = async (app: Vue, connectionsOptions: signalR.IHttpConnectionOptions): Promise<void> => {
    const dashboardSharedWorker = new DashboardSharedWorker();

    // Expose worker port globally for other components to use
    Vue.prototype.$workerPort = dashboardSharedWorker.port;

    // Add global methods for user settings via SharedWorker
    Vue.prototype.$saveUserSetting = (key: string, value: string) => {
        dashboardSharedWorker.port.postMessage(['saveUserSetting', { key, value }]);
    };

    Vue.prototype.$deleteUserSetting = (key: string) => {
        dashboardSharedWorker.port.postMessage(['deleteUserSetting', key]);
    };

    dashboardSharedWorker.port.start();

    //Send in the current token to get the hub party started
    dashboardSharedWorker.port.postMessage(["init", { serverURL: axios.defaults.baseURL?.replace("/api", "") }]);

    //Get messages back from the shared worker
    dashboardSharedWorker.port.onmessage = async function (e: any) {
        switch (e.data[0]) {
            case "data":
                app.$root.$data.dataHubState = e.data[1].status;
                app.$root.$data.dataHubFileVersion = e.data[1].fileVersion;
                // Update individual phoneStats properties to avoid triggering unnecessary re-renders
                if (e.data[1].phoneStats) {
                    Vue.set(app.$root.$data.dataHubPhoneStats, 'longestHold', e.data[1].phoneStats.longestHold);
                    Vue.set(app.$root.$data.dataHubPhoneStats, 'callsHolding', e.data[1].phoneStats.callsHolding);
                    Vue.set(app.$root.$data.dataHubPhoneStats, 'repsReady', e.data[1].phoneStats.repsReady);
                }
                app.$root.$data.notificationCount = e.data[1].notificationCount;
                app.$root.$data.dispenseErrorCount = e.data[1].dispenseErrorCount;
                app.$root.$data.taskCount = e.data[1].taskCount;
                app.$root.$data.dashboardData = new DashboardStatus(e.data[1].dashboardData);
                app.$root.$data.dashboardDataUpdated = e.data[1].dashboardDataDate;
                break;
            case "dataHubState":
                app.$root.$data.dataHubState = e.data[1];
                break;
            case "phoneStats":
                // Update individual properties to avoid triggering unnecessary re-renders
                if (e.data[1]) {
                    Vue.set(app.$root.$data.dataHubPhoneStats, 'longestHold', e.data[1].longestHold);
                    Vue.set(app.$root.$data.dataHubPhoneStats, 'callsHolding', e.data[1].callsHolding);
                    Vue.set(app.$root.$data.dataHubPhoneStats, 'repsReady', e.data[1].repsReady);
                }
                break;
            case "fileVersion":
                app.$root.$data.dataHubFileVersion = e.data[1];
                break;
            case "notificationCount":
                app.$root.$data.notificationCount = e.data[1];
                break;
            case "dispenseErrorCount":
                app.$root.$data.dispenseErrorCount = e.data[1];
                break;
            case "taskCount":
                app.$root.$data.taskCount = e.data[1];
                break;
            case "dashboardData":
                app.$root.$data.dashboardData = new DashboardStatus(e.data[1].data);
                app.$root.$data.dashboardDataUpdated = e.data[1].date;
                break;
            case "notification":
                console.debug("got message from shared worker", e);
                handleNotification(app, e.data[1]);
                break;
            case "userSettings":
                console.debug("received user settings from shared worker", e.data[1]);
                app.$root.$data.userSettings = e.data[1] || {};
                break;
            case "userSettingSaved":
                console.debug("user setting saved from shared worker", e.data[1]);
                if (e.data[1]?.key && e.data[1]?.value !== undefined) {
                    Vue.set(app.$root.$data.userSettings, e.data[1].key, e.data[1].value);
                }
                break;
            case "userSettingDeleted":
                console.debug("user setting deleted from shared worker", e.data[1]);
                if (e.data[1] && app.$root.$data.userSettings[e.data[1]]) {
                    Vue.delete(app.$root.$data.userSettings, e.data[1]);
                }
                break;
            case "phoneStatsDisabled":
                console.debug("phone stats disabled - TechQueue not configured on server");
                app.$root.$data.phoneStatsEnabled = false;
                break;
            case "textShortcodesChanged":
                console.debug("received text shortcodes changed from shared worker");
                app.$root.$data.textShortcodesVersion++;
                break;
            case "permissionsChanged":
                console.debug("received permissions changed notification", e.data[1]);
                try {
                    const { userInfoService } = await import("@/util/userInfoService");
                    await userInfoService.fetchUserInfo({ forceFresh: true });
                } catch (error) {
                    console.error("Failed to refresh permissions:", error);
                }
                break;
            case "requestToken":
                try {
                    if (!connectionsOptions.accessTokenFactory) throw "No accessTokenFactory";
                    const newToken = await connectionsOptions.accessTokenFactory();
                    console.log("MainApp: Obtained new token, sending 'tokenResponse' to Shared Worker.");
                    dashboardSharedWorker.port.postMessage(['tokenResponse', newToken]);
                } catch (error) {
                    console.error("MainApp: Failed to get new token via accessTokenFactory:", error);
                    dashboardSharedWorker.port.postMessage(['tokenResponse', null]);
                }
                break;
        }
    }
}

const handleDirectHubs = (app: Vue, serverURL: string, connectionsOptions: signalR.IHttpConnectionOptions): void => {
    const dataHub = new signalR.HubConnectionBuilder()
        .withAutomaticReconnect()
        .withUrl(`${serverURL}/data-hub`, connectionsOptions)
        .build();

    // Add global methods for user settings via direct hub
    Vue.prototype.$saveUserSetting = (key: string, value: string) => {
        return dataHub.invoke("SaveUserSetting", key, value);
    };

    Vue.prototype.$deleteUserSetting = (key: string) => {
        return dataHub.invoke("DeleteUserSetting", key);
    };

    dataHub
        .start()
        .then(() => {
            dataHub.on("PhoneStats", (res: PhoneStats) => {
                // Update individual properties to avoid triggering unnecessary re-renders
                if (res) {
                    Vue.set(app.$root.$data.dataHubPhoneStats, 'longestHold', res.longestHold);
                    Vue.set(app.$root.$data.dataHubPhoneStats, 'callsHolding', res.callsHolding);
                    Vue.set(app.$root.$data.dataHubPhoneStats, 'repsReady', res.repsReady);
                }
            });
            dataHub.on("FileVersion", (res) => {
                app.$root.$data.dataHubFileVersion = res;
            });
            dataHub.on("Notification", (res) => {
                if (res.ids) {
                    dataHub.invoke("AcknowledgeNotification", res.ids);
                } else {
                    dataHub.invoke("AcknowledgeNotification", res.id);
                }
                handleNotification(app, res);
            });
            dataHub.on("UpdateDispenseErrorCounter", (res) => {
                app.$root.$data.dispenseErrorCount = res;
            });
            dataHub.on("TaskCount", (res) => {
                app.$root.$data.taskCount = res;
            });
            dataHub.on("UpdateDashboard", (res: DashboardStatus) => {
                app.$root.$data.dashboardData = new DashboardStatus(res);
                app.$root.$data.dashboardDataUpdated = new Date();
            });

            // User Settings events for direct hub connection
            dataHub.on("UserSettings", (settings) => {
                console.debug("received user settings from direct hub", settings);
                app.$root.$data.userSettings = settings || {};
            });

            dataHub.on("UserSettingSaved", (data) => {
                console.debug("user setting saved from direct hub", data);
                if (data?.key && data?.value !== undefined) {
                    Vue.set(app.$root.$data.userSettings, data.key, data.value);
                }
            });

            dataHub.on("UserSettingDeleted", (key) => {
                console.debug("user setting deleted from direct hub", key);
                if (key && app.$root.$data.userSettings[key]) {
                    Vue.delete(app.$root.$data.userSettings, key);
                }
            });

            // Phone stats disabled event (TechQueue not configured on server)
            dataHub.on("PhoneStatsDisabled", () => {
                console.debug("phone stats disabled - TechQueue not configured on server");
                app.$root.$data.phoneStatsEnabled = false;
            });

            dataHub.on("TextShortcodesChanged", () => {
                console.debug("received text shortcodes changed from direct hub");
                app.$root.$data.textShortcodesVersion++;
            });

            dataHub.on("PermissionsChanged", async (data) => {
                console.log("Received PermissionsChanged event:", data);
                try {
                    const { userInfoService } = await import("@/util/userInfoService");
                    await userInfoService.fetchUserInfo({ forceFresh: true });
                } catch (error) {
                    console.error("Failed to refresh permissions:", error);
                }
            });

            dataHub.invoke("UpdateDashboard");
            dataHub.invoke("GetFileInfo");
            dataHub.invoke("GetUnreadNotificationCount");
            dataHub.invoke("GetTaskCount");
            dataHub.invoke("GetUserSettings");
            dataHub.invoke("GetPhoneStatsStatus");

            dataHub.on("NotificationUnreadCount", (res) => {
                app.$root.$data.notificationCount = res;
            });

            dataHub.onclose(() => { app.$root.$data.dataHubState = "Closed"; });
            dataHub.onreconnected(() => { app.$root.$data.dataHubState = "Connected"; });
            dataHub.onreconnecting(() => { app.$root.$data.dataHubState = "Reconnecting"; });
        })
        .catch((err: any) => {
            console.warn("Error while connecting to data-hub", {
                err,
                response: err?.response
            });
        });
}

export function dashboardSetup(app: Vue, serverURL: string, connectionsOptions: signalR.IHttpConnectionOptions): void {
    handlePrimaryTab();

    if (window.SharedWorker) {
        handleSharedWorker(app, connectionsOptions);
    } else {
        handleDirectHubs(app, serverURL, connectionsOptions);
    }
}
